import React, { useCallback, useState } from 'react';
import { AlertCircle } from 'lucide-react';
import { Modal } from '../../common/Modal';
import { useLanguage } from '../../../features/language/useLanguage';
import { inventoryService } from '../../../services/inventoryService';
import { UploadStep } from './UploadStep';
import { ReviewStep } from './ReviewStep';
import { ResultStep } from './ResultStep';
import { Stepper, type StepKey } from './Stepper';
import type { ImportRow } from './types';

interface Props {
  isOpen: boolean;
  onClose: () => void;
  onSuccess: () => void;
}

export const ImportGreenBeanModal: React.FC<Props> = ({
  isOpen,
  onClose,
  onSuccess,
}) => {
  const { t } = useLanguage();
  const [step, setStep] = useState<StepKey>('upload');
  const [rows, setRows] = useState<ImportRow[]>([]);
  const [createdCount, setCreatedCount] = useState(0);
  const [submitError, setSubmitError] = useState<string | null>(null);

  const reset = useCallback(() => {
    setStep('upload');
    setRows([]);
    setCreatedCount(0);
    setSubmitError(null);
  }, []);

  const handleClose = () => {
    reset();
    onClose();
  };

  const handleParsed = (parsed: ImportRow[]) => {
    setRows(parsed);
    setStep('review');
  };

  const handleSubmit = async (toSend: ImportRow[]) => {
    setSubmitError(null);
    try {
      const payload = toSend.map((r) => ({
        bean_name: r.beanName,
        description: r.description,
        processor_name: r.processorName,
        species: r.species,
        variety: r.variety,
        process_type: r.processType,
        initial_qty: r.initialQty,
        reorder_point: r.reorderPoint,
        standard_cost: r.standardCost,
      }));
      const resp = await inventoryService.importGreenBeans(payload);
      setCreatedCount(resp.created);
      setStep('result');
      onSuccess();
    } catch (e) {
      setSubmitError(t('inventory.import.errors.serverError'));
      // Swallow the throw so React doesn't see an unhandled rejection — the
      // user stays on Review and can fix + resubmit.
      console.error('Green bean import failed:', e);
    }
  };

  const stepLabels: Record<StepKey, string> = {
    upload: t('inventory.import.step1Title'),
    review: t('inventory.import.step2Title'),
    result: t('inventory.import.step3Title'),
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={handleClose}
      title={t('inventory.import.modalTitle')}
      maxWidth="6xl"
    >
      <div className="space-y-5">
        <div className="px-1">
          <Stepper current={step} labels={stepLabels} />
        </div>

        {submitError && (
          <div
            data-test-id="import-green-bean-submit-error"
            className="flex items-start gap-2 rounded-md border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700"
          >
            <AlertCircle className="h-4 w-4 mt-0.5 shrink-0" />
            <span>{submitError}</span>
          </div>
        )}

        <div key={step} className="relative">
          {step === 'upload' && <UploadStep onParsed={handleParsed} />}
          {step === 'review' && (
            <ReviewStep
              initialRows={rows}
              onBack={() => setStep('upload')}
              onSubmit={handleSubmit}
            />
          )}
          {step === 'result' && (
            <ResultStep count={createdCount} onDone={handleClose} />
          )}
        </div>
      </div>
    </Modal>
  );
};
